import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { assumeOptionsApi, assumeTenantApi } from 'services/AssumeTenantService';
import { getJwtPayload, isSuperAdmin, SUPER_ADMIN_ROLE } from 'utils/jwtRole';
import { loadTenantConfig, rememberTenantId } from 'config/tenantConfig';
import { injectTheme } from 'config/themeInjector';
import { persistProfileSnapshot, refreshStoredRolesPermissions } from 'utils/assumedSeat';
import { companyDetails } from 'utils/companyDetails';

const DEFAULT_ROLE = 1; // Admin

// Multi-tenancy "borrow a seat": the super admin picks a tenant + role and the
// auth service mints an ordinary tenant token (assumed:true) for that seat.
// UI convenience gate only — the assume APIs' own role-99 check is the real
// security wall.
function SelectTenant() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [role, setRole] = useState(DEFAULT_ROLE);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  // Role-picker modal: opens when a tenant row is clicked.
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  // Select a tenant and open the role-picker modal.
  const handleRowClick = (tenant) => {
    setSelectedTenantId(tenant.tenantId);
    setRole(DEFAULT_ROLE);
    setRoleModalOpen(true);
  };
  const closeRoleModal = () => {
    if (submitting) return;
    setRoleModalOpen(false);
  };

  const superAdmin = isSuperAdmin();

  useEffect(() => {
    if (!superAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await assumeOptionsApi();
        if (cancelled) return;
        const data = response?.data || {};
        setTenants(data.tenants || []);
        setRoles(data.roles || []);
        // Default to Admin (role 1) when offered, else the first option.
        const hasDefault = (data.roles || []).some(
          (option) => Number(option.role) === DEFAULT_ROLE,
        );
        if (!hasDefault && data.roles?.length) setRole(data.roles[0].role);
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || 'Failed to load tenant list');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [superAdmin]);

  if (!superAdmin) {
    return (
      <div className="p-5">
        <div className="p-5 bg-white borderStyle">Only the super admin can switch tenants.</div>
      </div>
    );
  }

  const selectedTenant = tenants.find((tenant) => tenant.tenantId === selectedTenantId);

  // Client-side search across the visible fields (the whole tenant list is
  // returned by assume-options, so filtering in-memory is sufficient).
  const query = search.trim().toLowerCase();
  const filteredTenants = query
    ? tenants.filter((t) =>
        [t.name, t.subdomain, t.address, t.phone, t.email, t.gstin]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(query)),
      )
    : tenants;

  const handleConfirm = async () => {
    if (!selectedTenant || submitting) return;
    setSubmitting(true);
    try {
      const response = await assumeTenantApi({
        tenantId: selectedTenant.tenantId,
        role: Number(role),
      });
      const data = response?.data;
      if (!response?.success || !data?.token) {
        throw new Error(response?.message || 'Failed to switch tenant');
      }

      // (a) Stash the platform token — only when the CURRENT token really is
      // the role-99 one, so an assumed token can never overwrite the stash.
      if (Number(getJwtPayload()?.role) === SUPER_ADMIN_ROLE) {
        localStorage.setItem('platformToken', localStorage.getItem('token'));
      }

      // (b) Become the tenant seat: swap the token, point x-tenant at the
      // target, drop the previous tenant's cached branding, and persist the
      // acting-as summary the banner renders.
      localStorage.setItem('token', data.token);
      const tenant = data.tenant || {};
      const tenantId = tenant.tenantId || selectedTenant.tenantId;
      rememberTenantId(tenantId);
      localStorage.removeItem('tenantConfig');
      localStorage.setItem(
        'actingAs',
        JSON.stringify({
          tenantId,
          name: tenant.name || selectedTenant.name || '',
          logo: tenant.logo || selectedTenant.logo || '',
          roleName: data.role?.roleName || String(role),
        }),
      );

      // (c) Mirror what login persists after OTP (OtpSaga → rolesPermissions +
      // profile). Persisted directly because the full-page navigation below
      // reboots redux anyway; landing on the first module matches
      // RolesAndPermissionsSaga's post-login replace.
      const modules = await refreshStoredRolesPermissions();
      await persistProfileSnapshot();
      const config = await loadTenantConfig();
      injectTheme(config);
      window.location.replace(`/${modules?.[0] || 'dashboard'}`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to switch tenant');
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5">
      <div className="p-5 bg-white borderStyle">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h2 className="text-lg font-semibold mb-1">Switch Tenant</h2>
          <Button variant="outlined" onClick={() => navigate('/tenants')}>
            Create tenant
          </Button>
        </div>
        <p className="text-gray-600 mb-4">
          Borrow a seat inside a tenant. You will act with the selected role until you exit.
        </p>

        {loading ? (
          <div className="p-5 text-center">
            <CircularProgress size={28} />
          </div>
        ) : !tenants.length ? (
          <p className="text-gray-600">No tenants available.</p>
        ) : (
          <>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, address, phone, email or GSTIN…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ marginBottom: 12, maxWidth: 420 }}
            />
            <TableContainer style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}>
              <Table size="small" aria-label="tenants">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>
                      <strong>Tenant</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Address</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Phone</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell>
                      <strong>GSTIN</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTenants.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        style={{ textAlign: 'center', color: '#888', padding: 24 }}
                      >
                        No tenants match &ldquo;{search.trim()}&rdquo;
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTenants.map((tenant) => {
                      const isSelected = tenant.tenantId === selectedTenantId;
                      return (
                        <TableRow
                          key={tenant.tenantId}
                          hover
                          selected={isSelected}
                          onClick={() => handleRowClick(tenant)}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            {/* Single-select: radio, never more than one row. */}
                            <Radio
                              size="small"
                              checked={isSelected}
                              onChange={() => handleRowClick(tenant)}
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ 'aria-label': `select ${tenant.name}` }}
                            />
                          </TableCell>
                          <TableCell>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <img
                                src={tenant.logo || companyDetails?.companyLogo}
                                alt=""
                                style={{
                                  height: 28,
                                  width: 28,
                                  objectFit: 'contain',
                                  borderRadius: 4,
                                  background: '#fff',
                                  flexShrink: 0,
                                }}
                              />
                              <span>
                                <span style={{ fontWeight: 600 }}>{tenant.name}</span>
                                {tenant.subdomain && (
                                  <span style={{ display: 'block', fontSize: 12, color: '#888' }}>
                                    {tenant.subdomain}
                                  </span>
                                )}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell>{tenant.address || '—'}</TableCell>
                          <TableCell>{tenant.phone || '—'}</TableCell>
                          <TableCell>{tenant.email || '—'}</TableCell>
                          <TableCell>{tenant.gstin || '—'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>

      {/* Role-picker modal — opens on a tenant row click. */}
      <Dialog open={roleModalOpen} onClose={closeRoleModal} maxWidth="xs" fullWidth>
        <DialogTitle>Enter {selectedTenant?.name || 'tenant'}</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>
            Pick the role you want to act as inside this tenant. You&apos;ll hold it until you exit
            the seat.
          </p>
          <TextField
            select
            fullWidth
            size="small"
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {(roles.length ? roles : [{ role: DEFAULT_ROLE, roleName: 'Admin' }]).map((option) => (
              <MenuItem key={option.role} value={option.role}>
                {option.roleName}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoleModal} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Switching...' : 'Enter tenant'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SelectTenant;
